import { Modules } from "config";
import type LocalizationService from "services/localizationService";
import TransferType from "entities/transferType";
import type EventBus from "services/eventBus";
import type SecurityService from "services/securityService";
import { action, computed, observable } from "mobx";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { bound } from "@frui.ts/helpers";
import { attachAutomaticValidator } from "@frui.ts/validation";
import BankExportFilters, { BankExportFilterValidationRules } from "entities/filters/bankExportFilter";
import type BankExportRepository from "repositories/bankExportRepository";
import type NotificationService from "services/notificationService";
import type OptionsService from "services/optionsService";
import type { IBankExport } from "entities/bankExport";
import { watchBusy } from "utils/busyWatcher";
import ListViewModel from "../../listViewModel";
import { allowToDownload, allowToDownloadFinance } from "./rights";

export default abstract class BankExportListViewModel<TEntity extends IBankExport = IBankExport> extends ListViewModel<
  TEntity,
  ScreenBase,
  ScreenBase,
  BankExportFilters
> {
  abstract download(item: TEntity): Promise<void>;
  readonly modelName = "bankExport";
  @observable items: TEntity[] = [];
  @observable fileResponse: any = undefined;

  protected constructor(
    protected repository: BankExportRepository,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService
  ) {
    super();
  }

  @computed
  get allowDelete() {
    return false;
  }

  @bound
  allowItemDetail(item: any) {
    return false;
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService) && !allowToDownloadFinance(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  @computed
  get stateOptions() {
    return this.optionsService.internalEnumOptions(TransferType);
  }

  @computed
  get lastDownloadedByOptions() {
    const formatOptions = (item: any) => ({ id: item.lastDownloadedBy, label: item.lastDownloadedBy });
    return this.items
      .filter(
        (bankExport, index, bankExportArray) =>
          bankExportArray.findIndex(currentBankExport => currentBankExport.lastDownloadedBy === bankExport.lastDownloadedBy) ===
          index
      )
      .map(formatOptions);
  }

  protected createFilter() {
    return attachAutomaticValidator(new BankExportFilters(), BankExportFilterValidationRules);
  }

  @action.bound
  @watchBusy
  async onDownload(item: TEntity) {
    await this.download(item);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `bankExports.${code}`, size);
  }

  @bound
  stateName(state: TransferType) {
    return this.stateOptions.find(item => item.id === state)?.label;
  }
}
